import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { customersService, leadsService, organizationService } from './services';
import { setOrgTimezone, formatInOrgTz } from './datetime';
import { DEAL_STAGES, isDealStage, toDeal } from './deals';

/* ============================================================
   SpectraIQ CRM data + actions — backend-backed.
   ------------------------------------------------------------
   Initial state is empty. On mount we fetch from /v1/customers
   and /v1/leads and map the responses into the UI shape every
   page already expects. Mutations are still in-memory for now
   (Sprint 6 wires backend writes for tags / notes / stage moves).
   ============================================================ */

// customersTotal is the server's count of ALL customers; `customers` is only the
// page the context loaded, so the two differ once the dealership outgrows a page.
const EMPTY_STATE = { customers: [], leads: [], deals: [], notes: {}, customersTotal: 0 };

/**
 * Canonical lead stages — these ARE the backend `LeadStage` enum values.
 *
 * Previously the UI invented its own five stages (NEW / AI QUALIFYING /
 * BOOKED / CONSULTANT / STALLED) and bucketed the backend's ten into them.
 * That made stage writes impossible: 'AI QUALIFYING' is not a LeadStage, so
 * the PATCH could never bind. We now speak the backend's language and only
 * translate at the presentation layer (STAGE_META below).
 */
/**
 * The agreed lead pipeline (E1, Ajay #1 / Ricky). Board columns render in this
 * order, so it must match the backend LeadStage enum exactly.
 *
 * Two things this list deliberately does NOT contain:
 *   - No AI stage. Ownership is `assistedBy` (AI / HUMAN), which overlays any
 *     stage — a lead can be Contacted AND AI-owned at once, and E5's shared
 *     mode needs exactly that.
 *   - No Show / No-show. Attendance is the appointment's status.
 * One fact, one place, in both cases.
 *
 * Sep 2026 lane rename (display only — keys are unchanged on the wire):
 *   APPOINTMENT → "Qualified", CONFIRMED → "Quoted", DELIVERY_BOOKED → "Booked".
 * RETAILED sits between Booked and Delivered; RETENTION was removed (V53).
 */
export const LEAD_STAGES = [
  'INBOUND',
  'CONTACTED',
  'APPOINTMENT',
  'TEST_DRIVE',
  'NEGOTIATION',
  'CONFIRMED',
  'DELIVERY_BOOKED',
  'RETAILED',
  'DELIVERED',
  'LOST',
];

/** Terminal stages — the backend rejects any transition out of these. */
export const CLOSED_STAGES = ['DELIVERED', 'LOST'];

/**
 * A lead is STALLED when it is still open and nothing has happened on it for
 * this many days. Mirror of StalledLeadRule.DAYS in the backend — keep them in
 * step. (Not the same as "unactioned", which is > 24h, nor "Lost", which is
 * closed, nor an at-risk CUSTOMER, which is 60+ days without contact.)
 */
export const STALLED_DAYS = 3;
export const isStalledLead = (l) => {
  if (!l || CLOSED_STAGES.includes(l.stage)) return false;
  const last = l.lastActivityAt || l.createdAt;
  return !!last && new Date(last).getTime() < Date.now() - STALLED_DAYS * 24 * 60 * 60 * 1000;
};

/** Human labels + colours for each stage. Display only — never sent over the wire. */
export const STAGE_META = {
  INBOUND: { label: 'Inbound', short: 'Inbound', color: 'var(--text-tertiary)' },
  CONTACTED: { label: 'Contacted', short: 'Contacted', color: 'var(--info)' },
  APPOINTMENT: { label: 'Qualified', short: 'Qualified', color: 'var(--accent-primary)' },
  TEST_DRIVE: { label: 'Test Drive', short: 'Test drive', color: 'var(--accent-primary)' },
  NEGOTIATION: { label: 'Negotiation', short: 'Negotiating', color: 'var(--warning)' },
  CONFIRMED: { label: 'Quoted', short: 'Quoted', color: 'var(--warning)' },
  DELIVERY_BOOKED: { label: 'Booked', short: 'Booked', color: 'var(--success)' },
  RETAILED: { label: 'Retailed', short: 'Retailed', color: 'var(--success)' },
  DELIVERED: { label: 'Delivered', short: 'Won', color: 'var(--success)' },
  LOST: { label: 'Lost', short: 'Lost', color: 'var(--danger)' },
};

export const stageLabel = (s) => STAGE_META[s]?.label || s || '—';
export const stageColor = (s) => STAGE_META[s]?.color || 'var(--text-tertiary)';
export const isClosedStage = (s) => CLOSED_STAGES.includes(s);

/** Deal pipeline = the commercial tail of the lead pipeline. See lib/deals.js. */
export { DEAL_STAGES };

/** "New customer (…5993)" — what to show for a customer who has not given a name yet. */
function unnamedCustomerLabel(phone) {
  const tail = String(phone || '').replace(/\D/g, '').slice(-4);
  return `New customer${tail ? ` (…${tail})` : ''}`;
}

/**
 * A minimal customer built from the name/phone the backend now returns with every
 * lead (`customerName`, `customerPhone`). Lets a lead screen show who it belongs to
 * immediately, before — or even without — the full customer record being loaded.
 */
export function customerFromLead(lead) {
  if (!lead || (!lead.customerName && !lead.customerPhone)) return undefined;
  const name = lead.customerName || unnamedCustomerLabel(lead.customerPhone);
  return {
    id: lead.customerId,
    name,
    initials: lead.customerName
      ? lead.customerName.split(' ').map((x) => x[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()
      : 'NC',
    identified: true,
    hasRealName: !!lead.customerName,
    phone: lead.customerPhone || '',
    email: '',
    partial: true,   // built from the lead, not the full customer record
  };
}

/* ─── Backend → UI shape mappers ─── */
function mapCustomer(c) {
  // A customer row can legitimately arrive with no PII at all: records created
  // by an inbound channel before the conversation surfaced a name, and imported
  // rows that never carried one. The backend omits null fields entirely
  // (@JsonInclude NON_NULL), so those objects are just {id, vip, createdAt}.
  //
  // Rendering an em dash for every column made those rows look like a broken
  // screen rather than an incomplete record. Fall back through what a person
  // could actually recognise the customer by — name, then email, then phone —
  // and only then to a short id, which at least identifies the row and can be
  // read out or searched for.
  const realName = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  const identified = !!(realName || c.email || c.phoneE164);
  // A bare phone number is not a name: until the AI agent has asked for and
  // saved the customer's name, show "New customer" (with the last digits so two
  // unnamed customers on the board can still be told apart). The full number
  // stays in the Contact column / `phone`.
  const name = realName
    || (c.phoneE164 ? unnamedCustomerLabel(c.phoneE164) : (c.email || `Customer ${String(c.id || '').slice(0, 8)}`));
  const initials = realName
    ? realName.split(' ').map(x => x[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()
    : (identified ? (c.phoneE164 && !c.email ? 'NC' : name.slice(0, 2).toUpperCase()) : '··');
  // Shown in the UI as the customer's label. `name` keeps the richer fallback
  // chain (phone/email/id) so search still matches; `displayName` is just the
  // real name or an explicit "No Name" placeholder, paired with `hasRealName`
  // to render it muted/italic.
  const displayName = realName || 'No Name';
  return {
    id: c.id,
    name,
    displayName,
    initials,
    /** False when the record carries no name, email or phone at all. */
    identified,
    /** True only when a real person's name is present (not an email/phone stand-in). */
    hasRealName: !!realName,
    phone: c.phoneE164 || '',
    email: c.email || '',
    since: c.createdAt ? c.createdAt.slice(0, 7) : '—',
    ltv: c.ltvAud || 0,
    /** Agent Test Console customer — the Dashboard does not count these. */
    simulated: !!c.simulated,
    churnRisk: c.churnRisk || 'LOW',
    churnReason: c.churnReason || '',
    tags: [...(c.tags || []), ...(c.vip ? ['VIP'] : [])],
    stage: c.stage || 'Customer',
    consents: c.consents || { sms: false, email: false, whatsapp: false, dnc: false },
    /** Source + date behind each channel's consent: { sms: { state, source, at } } — only channels with a record. */
    consentDetail: c.consentDetail || {},
    preferences: c.preferences || { channel: 'SMS', callWindow: 'any', lang: 'en-AU' },
    vehicles: c.vehicles || [],
    stats: c.stats || { ros: 0, avgRO: 0, vehiclesBought: 0, referrals: 0, totalSpend: 0 },
    nextAction: c.nextAction || '',
    activity: c.activity || [],
  };
}

function mapLead(l) {
  // The stage passes through untouched — the UI now uses the backend enum
  // verbatim. Anything unrecognised falls back to the pipeline entry stage.
  const stage = LEAD_STAGES.includes(l.stage) ? l.stage : 'INBOUND';

  return {
    id: l.id,
    customerId: l.customerId,
    // `customerName` is now resolved + decrypted server-side on the list
    // endpoint, so a real name shows even when the customer isn't in the loaded
    // customer set. Falls back to the vehicle of interest, then a consistent
    // "No Name" placeholder — never a bare "—" (DEF: some cards showed "—").
    name: l.customerName || l.vehicleModelRequested || 'No Name',
    score: l.score ?? 0,
    stage,
    source: l.source || 'WEB_FORM',
    sourceReference: l.sourceReference || null,
    vehicle: [l.vehicleMakeRequested, l.vehicleModelRequested].filter(Boolean).join(' ') || '—',
    vehicleMake: l.vehicleMakeRequested || '',
    vehicleModel: l.vehicleModelRequested || '',
    /** NEW / USED / DEMO, or '' when the lead has not been classified (E4). */
    vehicleCategory: l.vehicleCategory || '',
    vehicleOfInterestId: l.vehicleOfInterestId || null,
    assignedConsultantId: l.assignedConsultantId || null,
    assistedBy: l.assistedBy || 'AI',
    discoveryComplete: !!l.discoveryComplete,
    discoveryFlags: l.discoveryFlags || {},
    lostReasonCode: l.lostReasonCode || null,
    lostNotes: l.lostNotes || '',
    stageEnteredAt: l.stageEnteredAt || null,
    lastActivityAt: l.lastActivityAt || null,
    closedAt: l.closedAt || null,
    createdAt: l.createdAt,
    notes: l.notes || '',
    /** Agent Test Console data — shown on operational screens, excluded from reporting. */
    simulated: !!l.simulated,
  };
}

const CrmCtx = createContext(null);

// Polling cadence for leads (and customers). Short enough that a card the AI
// moves or re-scores shows up on the board within a few seconds.
const LEAD_POLL_MS = 5000;

// Backend ApiResponse<PageResponse<T>> = { success, data:{ content:[…] } }.
// Accept either the wrapped or unwrapped shape so callers don't care.
const unwrapList = (r) =>
  r?.data?.content ??
  r?.content ??
  (Array.isArray(r?.data) ? r.data : null) ??
  (Array.isArray(r) ? r : []);

/** Server-side total of a paged response, falling back to what was loaded. */
const totalOf = (r, loaded) => r?.data?.totalElements ?? r?.totalElements ?? loaded;

// ─────────────────────────────────────────────────────────────────────────────
// DEFERRED (client request pending): load EVERY page of leads/customers, with a
// light 5s poll + a full refresh each minute. Commented out for now — the app
// loads the first 100 rows as before. The full working version is saved in
// _parked/lead-accelerator-load-all/ (see README there).
// ─────────────────────────────────────────────────────────────────────────────
const PAGE_SIZE = 200;
// const MAX_ROWS = 5000;          // safety cap — beyond this, server-side paging is the next step
// const FULL_REFRESH_EVERY = 12;  // polls (12 × 5s = once a minute) between full re-reads
//
// /** Fetch every page of a paged list endpoint. Page 0 first, then the rest in parallel. */
// async function fetchAllPages(fetchPage) {
//   const first = await fetchPage(0);
//   let rows = [...unwrapList(first)];
//   const meta = first?.data && !Array.isArray(first.data) ? first.data : first;
//   const maxPages = Math.ceil(MAX_ROWS / PAGE_SIZE);
//   if (Number.isFinite(meta?.totalPages)) {
//     const pages = Math.min(meta.totalPages, maxPages);
//     if (pages > 1) {
//       const rest = await Promise.all(
//         Array.from({ length: pages - 1 }, (_, i) => fetchPage(i + 1)),
//       );
//       rest.forEach(r => { rows = rows.concat(unwrapList(r)); });
//     }
//   } else {
//     // No paging metadata — keep reading until a short page.
//     let page = 1;
//     let last = rows.length;
//     while (last >= PAGE_SIZE && page < maxPages) {
//       const r = unwrapList(await fetchPage(page));
//       rows = rows.concat(r);
//       last = r.length;
//       page += 1;
//     }
//   }
//   // Pages are read at slightly different moments, so a row can shift between
//   // them — keep one copy of each id.
//   const seen = new Map();
//   rows.forEach(r => { if (r?.id != null && !seen.has(r.id)) seen.set(r.id, r); });
//   return [...seen.values()];
// }
//
// const fetchAllLeads = () =>
//   fetchAllPages(p => leadsService.list({ page: p, size: PAGE_SIZE, includeSimulated: true }));
// const fetchAllCustomers = () =>
//   fetchAllPages(p => customersService.list({ page: p, size: PAGE_SIZE }));
//
// /** Overlay `updates` onto `prev` by id; rows only in `updates` are added at the front. */
// function mergeById(prev, updates) {
//   const byId = new Map(updates.map(u => [u.id, u]));
//   const merged = prev.map(p => (byId.has(p.id) ? { ...p, ...byId.get(p.id) } : p));
//   const known = new Set(prev.map(p => p.id));
//   const added = updates.filter(u => !known.has(u.id));
//   return added.length ? [...added, ...merged] : merged;
// }

export function CrmProvider({ children }) {
  const [state, setState] = useState(EMPTY_STATE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Customers fetched one-by-one because their lead's customer is not in the
  // 100-customer page. `tried` stops a failing id being re-requested every poll.
  const extraCustomerIds = useRef(new Set());
  const triedCustomerIds = useRef(new Set());
  // Stage moves that are saving right now (leadId -> stage the user dropped it
  // in). A poll that lands before the server has answered would otherwise put
  // the card back in its old column for a moment.
  const movingLeads = useRef(new Map());

  /**
   * Re-pull /v1/leads only and merge into state. Used by the 5-second
   * background poll AND by pages that just mutated a lead (Lead Detail
   * vehicle-interest edit, AI takeover) and want the kanban to reflect
   * the change immediately.
   */
  const refreshLeads = useCallback(async () => {
    try {
      const leadRes = await leadsService.list({ page: 0, size: PAGE_SIZE, includeSimulated: true }).catch(() => null);
      // A failed poll keeps the board as it is — it must not blank it.
      if (!leadRes) return null;
      const leads = unwrapList(leadRes).map(mapLead).map(l => (
        movingLeads.current.has(l.id) ? { ...l, stage: movingLeads.current.get(l.id) } : l
      ));
      setState(s => ({ ...s, leads }));
      return leads;
    } catch (_) {
      return null;
    }
  }, []);

  // DEFERRED — load-all version (see _parked/lead-accelerator-load-all/):
  // const refreshLeads = useCallback(async (opts) => {
  //   // `light`: only the most recently active page, merged into what is already
  //   // loaded (the cheap 5-second poll). Default: read every page and replace.
  //   const light = opts?.light === true;
  //   try {
  //     let rows;
  //     if (light) {
  //       const leadRes = await leadsService.list({ page: 0, size: PAGE_SIZE, includeSimulated: true }).catch(() => null);
  //       if (!leadRes) return null;
  //       rows = unwrapList(leadRes);
  //     } else {
  //       rows = await fetchAllLeads();
  //     }
  //     const fresh = rows.map(mapLead).map(l => (
  //       movingLeads.current.has(l.id) ? { ...l, stage: movingLeads.current.get(l.id) } : l
  //     ));
  //     setState(s => ({ ...s, leads: light ? mergeById(s.leads, fresh) : fresh }));
  //     return fresh;
  //   } catch (_) {
  //     return null;
  //   }
  // }, []);

  /**
   * Re-pull /v1/customers only and merge into state.
   *
   * Leads had this and customers did not, which is why a customer created
   * outside the browser — the voice agent taking a booking, an inbound SMS —
   * never appeared until the whole app was reloaded. The list was fetched once
   * on mount and cached for the session, so the screen showed a snapshot from
   * before the call happened and looked, convincingly, like the customer was
   * never created.
   *
   * A failure here does NOT clear the list: replacing good rows with an empty
   * array on a transient error is worse than showing slightly stale ones, and
   * "no customers" is indistinguishable from a brand-new dealership.
   */
  // DEFERRED — load-all version of this function's opening lines
  // (see _parked/lead-accelerator-load-all/):
  //   const refreshCustomers = useCallback(async (opts) => {
  //     const light = opts?.light === true;   // newest page only, merged (cheap poll)
  //     try {
  //       const rows = light
  //         ? unwrapList(await customersService.list({ page: 0, size: PAGE_SIZE }))
  //         : await fetchAllCustomers();
  //       const customers = rows.map(mapCustomer);
  //       setState(s => {
  //         if (light) return { ...s, customers: mergeById(s.customers, customers) };
  //         const fresh = new Set(customers.map(c => c.id));
  //         const extras = s.customers.filter(c => extraCustomerIds.current.has(c.id) && !fresh.has(c.id));
  //         return { ...s, customers: extras.length ? [...customers, ...extras] : customers };
  //       });
  const refreshCustomers = useCallback(async () => {
    try {
      const custRes = await customersService.list({ page: 0, size: PAGE_SIZE });
      const customers = unwrapList(custRes).map(mapCustomer);
      // Keep the customers fetched individually for leads outside this page
      // (see the effect below) — otherwise every poll would drop them again.
      setState(s => {
        const fresh = new Set(customers.map(c => c.id));
        const extras = s.customers.filter(c => extraCustomerIds.current.has(c.id) && !fresh.has(c.id));
        return {
          ...s,
          customers: extras.length ? [...customers, ...extras] : customers,
          customersTotal: totalOf(custRes, customers.length),
        };
      });
      setLoadError(null);
      return customers;
    } catch (e) {
      // Surfaced, not swallowed. The previous `.catch(() => null)` turned a
      // failed load into a silently empty screen.
      console.warn('[crm] customer refresh failed', e?.message);
      setLoadError(e?.message || 'Could not refresh customers');
      return null;
    }
  }, []);

  // Initial load — customers + leads in parallel.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // allSettled, not all-with-catch: a failed customer load must be
        // distinguishable from a dealership that genuinely has no customers.
        // The old `.catch(() => null)` collapsed both into an empty screen with
        // nothing logged and nothing shown.
        const [custOut, leadOut, orgOut] = await Promise.allSettled([
          customersService.list({ page: 0, size: PAGE_SIZE }),
          leadsService.list({ page: 0, size: PAGE_SIZE, includeSimulated: true }),
          // DEFERRED (load-all): fetchAllCustomers(), fetchAllLeads(),
          // Hydrate the org timezone so every date renders in the dealership's
          // local clock. Genuinely non-fatal — the helper defaults to
          // Australia/Sydney.
          organizationService.get(),
        ]);
        if (cancelled) return;

        const orgRes = orgOut.status === 'fulfilled' ? orgOut.value : null;
        if (orgRes?.timezone) setOrgTimezone(orgRes.timezone);

        const customers = custOut.status === 'fulfilled'
          ? unwrapList(custOut.value).map(mapCustomer) : [];
        const leads = leadOut.status === 'fulfilled'
          ? unwrapList(leadOut.value).map(mapLead) : [];

        const failures = [
          custOut.status === 'rejected' ? 'customers' : null,
          leadOut.status === 'rejected' ? 'leads' : null,
        ].filter(Boolean);

        if (failures.length) {
          const why = custOut.reason?.message || leadOut.reason?.message || 'request failed';
          console.warn(`[crm] could not load ${failures.join(' and ')}: ${why}`);
          setLoadError(`Could not load ${failures.join(' and ')} — ${why}`);
        }

        setState({
          customers, leads, deals: [], notes: {},
          customersTotal: custOut.status === 'fulfilled' ? totalOf(custOut.value, customers.length) : 0,
        });
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || 'load failed');
          setState(EMPTY_STATE);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Background poll — every LEAD_POLL_MS while the tab is visible.
  // Pauses when the tab is in the background (no point burning bandwidth
  // on data the user isn't looking at).
  useEffect(() => {
    let timer = null;

    // DEFERRED (load-all): most polls re-read only the newest page and merge it;
    // every FULL_REFRESH_EVERY-th re-reads everything:
    //   let polls = 0;
    //   const tick = () => {
    //     if (document.visibilityState === 'visible') {
    //       polls += 1;
    //       const opts = { light: polls % FULL_REFRESH_EVERY !== 0 };
    //       refreshLeads(opts);
    //       refreshCustomers(opts);
    //     }
    //   };
    const tick = () => {
      if (document.visibilityState === 'visible') {
        refreshLeads();
        // Customers change from outside the browser too — the voice agent
        // creates one on every first-time caller — so they are polled on the
        // same cadence rather than being frozen at whatever existed on mount.
        refreshCustomers();
      }
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, LEAD_POLL_MS);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Catch up on what we missed while hidden — the common case being a
        // consultant who switched tabs to take a call and came back.
        refreshLeads();
        refreshCustomers();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshLeads, refreshCustomers]);

  /**
   * Safety net: the customer list above is only the 100 most recently updated
   * customers, so a lead whose customer is outside it would show no customer at
   * all. Fetch those customers one by one (at most 25 per pass) and keep them.
   * A failed id is not retried, so a dead link cannot spin the network.
   */
  useEffect(() => {
    const known = new Set(state.customers.map(c => c.id));
    const missing = [...new Set(state.leads.map(l => l.customerId))]
      .filter(id => id && !known.has(id) && !triedCustomerIds.current.has(id))
      .slice(0, 25);
    if (missing.length === 0) return;
    missing.forEach(id => triedCustomerIds.current.add(id));

    Promise.allSettled(missing.map(id => customersService.get(id))).then((results) => {
      const found = results
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value?.data ?? r.value)
        .filter(c => c && c.id)
        .map(mapCustomer);
      if (found.length === 0) return;
      found.forEach(c => extraCustomerIds.current.add(c.id));
      setState(s => {
        const have = new Set(s.customers.map(c => c.id));
        const add = found.filter(c => !have.has(c.id));
        return add.length ? { ...s, customers: [...s.customers, ...add] } : s;
      });
    });
  }, [state.leads, state.customers]);

  /* ── Customer actions ── */
  const getCustomer = useCallback(
    (id) => state.customers.find(c => c.id === id),
    [state.customers],
  );

  /**
   * Build the backend upsert request from the loose form shape the modal
   * collects. Splits "Full Name" into first/last so it persists correctly.
   */
  const toUpsertRequest = (input) => {
    const trimmed = (v) => (v == null ? null : String(v).trim());
    const name = trimmed(input.name) || '';
    let firstName = input.firstName ?? null;
    let lastName = input.lastName ?? null;
    if (!firstName && !lastName && name) {
      const parts = name.split(/\s+/);
      firstName = parts.shift() || null;
      lastName = parts.join(' ') || null;
    }
    return {
      firstName,
      lastName,
      phoneE164: trimmed(input.phone) || trimmed(input.phoneE164),
      email: trimmed(input.email),
      vip: typeof input.vip === 'boolean' ? input.vip : undefined,
      fleetAccountId: trimmed(input.fleetAccountId),
    };
  };

  /**
   * Create on the backend, then merge the returned row into state. Returns
   * a Promise so the modal can await + toast / close on success.
   */
  const addCustomer = useCallback(async (input) => {
    const req = toUpsertRequest(input);
    const created = await customersService.create(req);
    if (created?.id) {
      const mapped = mapCustomer(created);
      // Carry over UI-only fields the modal may have collected (tags, stage).
      if (Array.isArray(input.tags) && input.tags.length) mapped.tags = input.tags;
      setState(s => ({ ...s, customers: [mapped, ...s.customers], customersTotal: (s.customersTotal || 0) + 1 }));
      return mapped;
    }
    // Fallback: still surface something in the UI even if the response was odd.
    const local = mapCustomer({ id: `local-${Date.now()}`, ...input });
    setState(s => ({ ...s, customers: [local, ...s.customers] }));
    return local;
  }, []);

  /**
   * Edit an existing customer. The patch may be partial — only PUT the
   * fields that map onto the backend request shape. UI-only fields (tags,
   * stage, churnRisk, etc.) stay client-side for now.
   */
  const updateCustomer = useCallback(async (id, patch) => {
    // Detect whether the caller is editing identity/contact fields the
    // backend cares about, vs. just UI-only fields (tags, nextAction…).
    const backendKeys = ['name', 'firstName', 'lastName', 'phone', 'phoneE164', 'email', 'vip', 'fleetAccountId'];
    const hasBackendChange = backendKeys.some(k => Object.prototype.hasOwnProperty.call(patch, k));

    // Optimistic merge — the UI updates instantly. We roll back on failure.
    let prev;
    setState(s => {
      prev = s.customers.find(c => c.id === id);
      return {
        ...s,
        customers: s.customers.map(c => (c.id === id ? { ...c, ...patch } : c)),
      };
    });

    if (!hasBackendChange) return;
    try {
      const req = toUpsertRequest(patch);
      const saved = await customersService.update(id, req);
      if (saved?.id) {
        const mapped = mapCustomer(saved);
        setState(s => ({
          ...s,
          customers: s.customers.map(c => (c.id === id ? { ...c, ...mapped, tags: c.tags } : c)),
        }));
      }
    } catch (err) {
      // Roll back the optimistic merge.
      if (prev) {
        setState(s => ({
          ...s,
          customers: s.customers.map(c => (c.id === id ? prev : c)),
        }));
      }
      throw err;
    }
  }, []);

  /* ── Notes (in-memory; not persisted) ── */
  const addNote = useCallback((customerId, text, author = 'You') => {
    if (!text || !text.trim()) return;
    setState(s => {
      const list = s.notes[customerId] || [];
      const ts = formatInOrgTz(new Date(), {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
      });
      const newNote = { id: `N-${Date.now()}`, ts, author, text };
      return { ...s, notes: { ...s.notes, [customerId]: [newNote, ...list] } };
    });
  }, []);

  const deleteNote = useCallback((customerId, noteId) => {
    setState(s => {
      const list = (s.notes[customerId] || []).filter(n => n.id !== noteId);
      return { ...s, notes: { ...s.notes, [customerId]: list } };
    });
  }, []);

  /* ── Tags ── */
  const toggleTag = useCallback((id, tag) => {
    setState(s => ({
      ...s,
      customers: s.customers.map(c => {
        if (c.id !== id) return c;
        const has = (c.tags || []).includes(tag);
        return { ...c, tags: has ? c.tags.filter(t => t !== tag) : [...(c.tags || []), tag] };
      }),
    }));
  }, []);

  /**
   * Lead stage move — optimistic, then PATCH /v1/leads/{id}/stage?to=STAGE.
   *
   * Returns a Promise so callers can await + toast the real API error instead
   * of silently swallowing it. On failure the optimistic move is rolled back
   * to the *previous* stage (the old implementation rolled back to `l.stage`,
   * which by then was already the new value — i.e. it never rolled back).
   *
   * `to=LOST` requires opts.lostReasonCode; the backend rejects it otherwise
   * with LOST_REASON_REQUIRED.
   */
  const moveLead = useCallback(async (leadId, stage, opts = {}) => {
    if (!LEAD_STAGES.includes(stage)) {
      throw new Error(`Unknown lead stage: ${stage}`);
    }

    movingLeads.current.set(leadId, stage);
    let previous = null;
    setState(s => {
      previous = s.leads.find(l => l.id === leadId)?.stage ?? null;
      return {
        ...s,
        leads: s.leads.map(l => (l.id === leadId ? { ...l, stage } : l)),
      };
    });

    try {
      const body = {};
      if (opts.lostReasonCode) body.lostReasonCode = opts.lostReasonCode;
      if (opts.lostNotes) body.lostNotes = opts.lostNotes;

      const saved = await leadsService.updateStage(leadId, stage, body);
      // Reconcile with what the server actually persisted.
      if (saved?.id) {
        const mapped = mapLead(saved);
        setState(s => ({
          ...s,
          leads: s.leads.map(l => (l.id === leadId ? { ...l, ...mapped } : l)),
        }));
      }
      return saved;
    } catch (err) {
      if (previous) {
        setState(s => ({
          ...s,
          leads: s.leads.map(l => (l.id === leadId ? { ...l, stage: previous } : l)),
        }));
      }
      throw err;
    } finally {
      movingLeads.current.delete(leadId);
    }
  }, []);

  /**
   * Deal stage move. A deal IS a lead in a late stage, so this delegates
   * straight to moveLead — the drag persists to the backend rather than
   * evaporating on refresh.
   */
  const moveDeal = useCallback((dealId, stage, opts = {}) => {
    if (!DEAL_STAGES.includes(stage)) {
      throw new Error(`Unknown deal stage: ${stage}`);
    }
    return moveLead(dealId, stage, opts);
  }, [moveLead]);

  /* ── Reset — re-fetches from backend. ── */
  const resetData = useCallback(() => {
    setState(EMPTY_STATE);
    // Trigger the load effect by setting state then forcing a remount-like effect.
    // Simplest: page reload — pages reading useCrm will see empty + then re-fetch.
    if (typeof window !== 'undefined') window.location.reload();
  }, []);

  /**
   * Deals are DERIVED from leads, not stored.
   *
   * `state.deals` was always an empty array — nothing ever wrote to it — so
   * every consumer (Dashboard tiles, Customer 360 deal list) silently
   * rendered zeros. A deal is just a lead that reached a commercial stage,
   * so we compute it. Values here are un-priced (no vehicle join); pages
   * that need real driveaway prices use useVehicleMap + toDeal directly.
   */
  const derivedDeals = useMemo(
    () => state.leads
      .filter(l => isDealStage(l.stage))
      .map(l => toDeal(l, state.customers.find(c => c.id === l.customerId), null)),
    [state.leads, state.customers],
  );

  const value = useMemo(() => ({
    customers: state.customers,
    customersTotal: Math.max(state.customersTotal || 0, state.customers.length),
    leads: state.leads,
    deals: derivedDeals,
    notes: state.notes,
    loading,
    loadError,
    getCustomer,
    addCustomer,
    updateCustomer,
    addNote,
    deleteNote,
    toggleTag,
    moveLead,
    moveDeal,
    resetData,
    refreshLeads,
    // Exposed so a page that just mutated a customer — or knows one was
    // created out of process — can pull the list without a full reload.
    refreshCustomers,
  }), [state, derivedDeals, loading, loadError, getCustomer, addCustomer, updateCustomer, addNote, deleteNote, toggleTag, moveLead, moveDeal, resetData, refreshLeads, refreshCustomers]);

  return <CrmCtx.Provider value={value}>{children}</CrmCtx.Provider>;
}

export function useCrm() {
  const ctx = useContext(CrmCtx);
  if (!ctx) throw new Error('useCrm() must be used inside <CrmProvider>');
  return ctx;
}
